import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { TranscriptFailureReason } from "@/entities/activities/types";
import { cn } from "@/utils/utils";

interface Props {
  reason: TranscriptFailureReason;
  className?: string;
}

// Total Record, so adding a member to the union without copy here is a compile error.
const SUBTEXT_KEYS: Record<TranscriptFailureReason, string> = {
  never_admitted: "activities.transcript.unavailableNeverAdmitted",
  bot_fatal: "activities.transcript.unavailableBotFatal",
  transcript_processing: "activities.transcript.unavailableProcessingFailed"
};

// The generic terminal-failure copy, also used for a reason this build cannot map.
const GENERIC_SUBTEXT_KEY = "activities.transcript.unavailableProcessingFailed";

// reason crosses an API boundary, so it can hold a value outside this build's union
// — the enum lives in accountsession and normally deploys ahead of the frontend.
// Falling back keeps the notice readable; t() on an unmapped key would render an
// empty subtext, leaving the title with no explanation and nothing logged.
// hasOwnProperty rather than ??: an inherited key such as "__proto__" resolves to a
// member of Object.prototype, so ?? would never fire.
const subtextKeyFor = (reason: TranscriptFailureReason): string =>
  Object.prototype.hasOwnProperty.call(SUBTEXT_KEYS, reason)
    ? SUBTEXT_KEYS[reason]
    : GENERIC_SUBTEXT_KEY;

/**
 * Terminal "no transcript" notice for a meeting activity. Without it a meeting the
 * notetaker never captured is indistinguishable from one nobody has filled in yet,
 * so users assume the recording was lost rather than that the bot was never let in.
 */
const TranscriptUnavailableNotice: React.FC<Props> = ({
  reason,
  className
}) => {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      className={cn(
        "flex gap-2 items-start p-2 rounded-lg bg-white-4",
        className
      )}
    >
      <IconContainer size="5" color="warning" className="mt-0.5">
        <AlertTriangle />
      </IconContainer>
      <div className="flex flex-col gap-0.5 min-w-0">
        <Text variant="label">
          {t("activities.transcript.unavailableTitle")}
        </Text>
        <Text variant="bodySecondary">{t(subtextKeyFor(reason))}</Text>
      </div>
    </div>
  );
};

export default TranscriptUnavailableNotice;
